import React, { useEffect, useRef, useState } from "react";

interface Props extends Omit<React.InputHTMLAttributes<HTMLInputElement>,
    "inputMode" | "max" | "min" | "onChange" | "step" | "type" | "value"> {
    value: number;
    locale?: string;
    min?: number;
    max?: number;
    onValueChange: (value: number) => void;
}

export const formatMinorUnitValue = (value: number, locale?: string) =>
    new Intl.NumberFormat(locale, {
        maximumFractionDigits: 2,
        minimumFractionDigits: 2,
        useGrouping: false,
    }).format((Number.isFinite(value) ? value : 0) / 100);

export const parseMinorUnitValue = (value: string): number | null => {
    const normalized = value.trim().replace(",", ".");
    if (!normalized || normalized === "-" || normalized === "." || normalized === "-.") {
        return null;
    }
    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? Math.round(parsed * 100) : null;
};

const MinorUnitInput: React.FC<Props> = ({
    value,
    locale,
    min = 0,
    max,
    onValueChange,
    onBlur,
    onFocus,
    ...inputProps
}) => {
    const [text, setText] = useState(() => formatMinorUnitValue(value, locale));
    const focused = useRef(false);
    const pattern = min < 0 ? /^-?\d*(?:[.,]\d{0,2})?$/ : /^\d*(?:[.,]\d{0,2})?$/;

    const clamp = (minorUnits: number) => {
        const minimum = Math.round(min * 100);
        const maximum = max === undefined ? Number.POSITIVE_INFINITY : Math.round(max * 100);
        return Math.min(maximum, Math.max(minimum, minorUnits));
    };

    useEffect(() => {
        if (!focused.current) {
            setText(formatMinorUnitValue(value, locale));
        }
    }, [locale, value]);

    return <input
        {...inputProps}
        className={inputProps.className ?? "form-control"}
        inputMode="decimal"
        type="text"
        value={text}
        onChange={event => {
            const next = event.target.value;
            if (!pattern.test(next)) return;
            setText(next);
            const parsed = parseMinorUnitValue(next);
            if (parsed !== null) onValueChange(clamp(parsed));
        }}
        onFocus={event => {
            focused.current = true;
            onFocus?.(event);
        }}
        onBlur={event => {
            focused.current = false;
            const normalized = clamp(parseMinorUnitValue(text) ?? Math.round(min * 100));
            onValueChange(normalized);
            setText(formatMinorUnitValue(normalized, locale));
            onBlur?.(event);
        }}
    />;
};

export default MinorUnitInput;
